import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface BehaviourSummary {
  name: string;
  points_this_week: number;
  negatives_this_week: number;
  points_last_week: number;
  negatives_last_week: number;
  points_term: number;
  negatives_term: number;
  points_year: number;
  negatives_year: number;
}

interface BehaviourSummaryResponse {
  data: BehaviourSummary[];
  meta: {
    lastSyncedAt: string | null;
    lastSyncStatus: 'success' | 'error' | 'running' | 'idle';
    lastSyncError?: string;
  };
}

interface BehaviourIncident {
  incident_type: 'positive' | 'negative';
  incident_date: string;
  incident_time: string | null;
  points: number | null;
  category: string | null;
  behaviour: string | null;
  awarded_by: string | null;
  event: string | null;
}

export function useBehaviourSummary() {
  return useQuery<BehaviourSummaryResponse>({
    queryKey: ['behaviour', 'summary'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/behaviour/summary`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch behaviour summary');
      return res.json();
    },
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });
}

export function useBehaviourIncidents(child: string, period: 'week' | 'term', customWeekStart?: Date) {
  const queryKey = customWeekStart
    ? ['behaviour', 'incidents', child, customWeekStart.toISOString()]
    : ['behaviour', 'incidents', child, period];

  return useQuery<BehaviourIncident[]>({
    queryKey,
    queryFn: async () => {
      let url = `${API_URL}/behaviour/incidents?child=${child}&period=${period}`;
      if (customWeekStart) {
        url += `&weekStart=${customWeekStart.toISOString()}`;
      }
      const res = await fetch(url, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch incidents');
      return res.json();
    },
  });
}

export function useSyncBehaviour() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/behaviour/sync`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to sync behaviour data');
      return res.json();
    },
    onSuccess: () => {
      // Invalidate all behaviour queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ['behaviour'] });
    },
  });
}
